import React from 'react';
import { Price } from '@noths/polaris-client-ribbons-design-system';
import { Icon } from '@noths/polaris-design-system-core-components';

import type { ProductState } from 'src/redux/product/types';
import { roundAverageProductRating } from 'src/utils/roundAverageProductRating';
import * as styles from './ProductPriceAndRating.styles';

export const CURRENT_PRICE_TEST_ID = 'current-price';

export interface ProductPriceAndRatingProps {
  averageRating?: number;
  mostLoved?: boolean;
  numberOfReviews?: number;
  onReviewsLinkClick?: (evt: React.MouseEvent<HTMLAnchorElement>) => void;
  preSalePrices?: ProductState['preSalePrices'];
  price: ProductState['price'];
  priceIsMinimum?: boolean;
  reviewsLinkHref: string;
  salePercentage?: number;
}

export const ProductPriceAndRating = ({
  averageRating,
  mostLoved = false,
  numberOfReviews = 0,
  onReviewsLinkClick = () => {},
  preSalePrices,
  price,
  priceIsMinimum = false,
  reviewsLinkHref,
  salePercentage = 0,
}: ProductPriceAndRatingProps) => {
  const { currency } = price;
  const preSalePrice = preSalePrices?.find((preSalePrice) => preSalePrice.currency === currency);
  const showRating = Boolean(averageRating);
  const formattedNumberOfReviews = numberOfReviews.toLocaleString('en-GB');

  return (
    <section css={styles.root}>
      <div css={styles.prices}>
        {priceIsMinimum && <span css={styles.from}>From</span>}
        <Price
          amount={price.amount}
          css={styles.currentPrice}
          currency={currency}
          testId={CURRENT_PRICE_TEST_ID}
          type={preSalePrice !== undefined ? 'sale' : 'normal'}
        />
        {(preSalePrice || salePercentage > 0) && (
          <div css={styles.preSaleInfo}>
            {preSalePrice && (
              <Price
                amount={preSalePrice.amount}
                css={styles.previousPrice}
                currency={currency}
                type="previous"
              />
            )}
            {salePercentage > 0 && <span css={styles.percentageOff}>{salePercentage}% off</span>}
          </div>
        )}
      </div>
      {(mostLoved || showRating) && (
        <div css={styles.rating}>
          {showRating && (
            <a css={styles.reviewsLink} href={reviewsLinkHref} onClick={onReviewsLinkClick}>
              <b>{roundAverageProductRating(averageRating)}</b>
              <Icon name="starFull" size="sm" />
              {numberOfReviews && <small>({formattedNumberOfReviews})</small>}
            </a>
          )}
          {mostLoved && (
            <span css={styles.mostLoved}>
              <Icon name="mostLoved" size="sm" /> Most loved
            </span>
          )}
        </div>
      )}
    </section>
  );
};
